/**
 * Card link helper.
 *
 * Only base card numbers own a URL (`/cards/EBP01-025`); parallels such as
 * ETD01-024TSR share that page, and `/cards/$code` 301s a variant code back to
 * its base. Linking a variant code directly therefore looked broken — a
 * $204 TSR row landed on the plain ETD01-024 page with no sign of the printing
 * the user clicked.
 *
 * `cardLinkProps` keeps one indexable URL per card but carries the printing the
 * user clicked in a `printing` search param, so the detail page can open on and
 * highlight the exact parallel.
 */
import { OFFICIAL_CARD_BY_CODE, normalizeCardCode } from "./tcg-cards";

/** Base (URL-owning) code for any printed code; falls back to the input. */
export function baseCodeOf(code: string): string {
  const hit = OFFICIAL_CARD_BY_CODE.get(normalizeCardCode(code));
  return (hit?.base ?? hit?.code ?? code).toUpperCase();
}

/** True when the code is a parallel printing rather than the base card. */
export function isVariantCode(code: string): boolean {
  return baseCodeOf(code) !== normalizeCardCode(code).toUpperCase();
}

/**
 * `<Link {...cardLinkProps(code)}>` — correct destination for any printing.
 *
 * A `?printing=` URL is a user convenience, not a separate document: it renders
 * the base card page and canonicalises to it. Marking those links `nofollow`
 * keeps Google from queueing them as crawlable duplicates, which is what
 * surfaced as "Alternate page with proper canonical tag" in Search Console.
 */
export function cardLinkProps(code: string) {
  const base = baseCodeOf(code);
  const printed = normalizeCardCode(code).toUpperCase();
  const variant = printed !== base;
  return {
    to: "/cards/$code" as const,
    params: { code: base },
    search: (variant ? { printing: printed } : {}) as { printing?: string },
    ...(variant ? { rel: "nofollow" } : {}),
  };
}

/** Absolute-path form for sitemaps, feeds and citation strings. */
export function cardHref(code: string): string {
  const base = baseCodeOf(code);
  const printed = normalizeCardCode(code).toUpperCase();
  return printed === base ? `/cards/${base}` : `/cards/${base}?printing=${printed}`;
}
